/**
 * Suite TTS — forge-backend engines via the public suite relay (CF origin + Neon).
 * POST /api/tts/synthesize { text, modelId, voice, client: "voiceforge" }
 * Debit client: voiceforge (wallet on ProxyPro).
 */

import { getAccessToken } from "../../suite/auth";
import { isBreezeHouse, isKokoroHouse } from "../houseEngines";
import { costHintForModel, DEFAULT_SUITE_MODEL_ID, relayBase, type TtsModelOpt } from "../ttsCatalog";
import type { TtsRequest, TtsResult } from "./types";

/** Rough cost when catalog has no per-model figure. */
export const SUITE_TTS_COST_HINT_USD = 0.015;

const SUITE_KOKORO_ENDPOINT =
  "https://ff8togjx2kt0mv8u.us-east-1.aws.endpoints.huggingface.cloud";
const SUITE_KOKORO_HF_NAME = "vividlex-kokoro";

function isKokoroModel(id: string): boolean {
  return isKokoroHouse(id) || /kokoro|hexgrad\/kokoro|forge\/suite-kokoro/i.test(id);
}

export async function suiteTts(
  req: TtsRequest,
  catalog?: TtsModelOpt[],
): Promise<TtsResult> {
  const token = getAccessToken();
  if (!token) {
    return {
      ok: false,
      error: "Sign in with Google to use suite voice engines.",
      backend: "suite",
      costUsdHint: SUITE_TTS_COST_HINT_USD,
    };
  }

  const modelId = (req.model || DEFAULT_SUITE_MODEL_ID).trim();
  const voice = (req.voice || "").trim() || undefined;
  const costUsdHint =
    catalog && catalog.length
      ? costHintForModel(catalog, modelId)
      : SUITE_TTS_COST_HINT_USD;

  const relay = relayBase();
  // Primary BookForge path (same as chapter Gen audio).
  // Send both voice and voice_id — ElevenLabs CF /ai/run requires voice_id.
  const attempts: { path: string; body: Record<string, unknown> }[] = [
    {
      path: "/api/tts/synthesize",
      body: {
        text: req.text,
        modelId,
        model: modelId,
        voice,
        voice_id: voice,
        client: "voiceforge",
        tool: "tts",
        speed: req.speed,
        emotion: req.emotion,
        instructions: req.instructions,
        parameters: req.parameters,
        ...(isKokoroModel(modelId)
          ? {
              modelId: modelId.toLowerCase().includes("fal")
                ? modelId
                : "forge/suite-kokoro",
              model: modelId.toLowerCase().includes("fal")
                ? modelId
                : "forge/suite-kokoro",
              kokoroEndpoint: SUITE_KOKORO_ENDPOINT,
              kokoroEndpointName: SUITE_KOKORO_HF_NAME,
            }
          : {}),
        ...(isBreezeHouse(modelId)
          ? {
              modelId: "forge/suite-breeze-tts",
              model: "forge/suite-breeze-tts",
            }
          : {}),
      },
    },
    // OpenAI-shape fallback only when synthesize is missing (404/405/501)
    {
      path: "/v1/audio/speech",
      body: {
        model: modelId.includes("tts") ? modelId : "tts-1",
        input: req.text,
        voice: voice || "alloy",
        response_format: req.format || "mp3",
        speed: req.speed ?? 1,
      },
    },
  ];

  const errors: string[] = [];
  for (let i = 0; i < attempts.length; i++) {
    const a = attempts[i]!;
    const wakeTries = a.path.includes("/api/tts/synthesize") ? 12 : 1;
    try {
      let res: Response | null = null;
      let lastWakeMsg = "";
      for (let w = 0; w < wakeTries; w++) {
        // Headers must stay within bookforge-relay CORS allowlist
        // (Content-Type, Authorization, X-Requested-With, Stripe-Signature).
        // Extra headers like X-Client-Product fail preflight → "Failed to fetch".
        res = await fetch(`${relay}${a.path}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(a.body),
          signal: AbortSignal.timeout(180000),
        });
        if (res.status !== 503) break;
        const t = await res.text().catch(() => "");
        lastWakeMsg = t.slice(0, 200);
        try {
          const j = JSON.parse(t) as { error?: string; code?: string };
          lastWakeMsg = j.error || j.code || lastWakeMsg;
        } catch {
          /* keep text */
        }
        if (!/wak/i.test(`${res.status} ${lastWakeMsg}`)) break;
        await new Promise((r) => setTimeout(r, 12_000));
      }
      if (!res) continue;
      if (res.status === 404 || res.status === 405 || res.status === 501) {
        errors.push(`${a.path}: ${res.status}`);
        continue;
      }
      if (!res.ok) {
        const t = lastWakeMsg || (await res.text().catch(() => ""));
        let msg = t.slice(0, 200);
        try {
          const j = JSON.parse(t) as { error?: string };
          if (j.error) msg = j.error;
        } catch {
          /* keep text */
        }
        if (/not available via unified billing|use BYOK/i.test(msg)) {
          msg =
            "This ElevenLabs model is not on Cloudflare Unified Billing (Flash is BYOK-only). Use Multilingual v2, Turbo v2.5, or v3 — no ElevenLabs key needed.";
        }
        errors.push(`${a.path}: ${res.status} ${msg}`);
        if (res.status === 402 || res.status === 403) {
          return {
            ok: false,
            error: msg || "Insufficient suite credits or access denied.",
            backend: "suite",
            costUsdHint,
          };
        }
        // 400/5xx from synthesize is a real engine error — don't mask with /v1 404
        return {
          ok: false,
          error: errors.join(" · ") || msg,
          backend: "suite",
          costUsdHint,
        };
      }
      const ct = res.headers.get("content-type") || "";
      if (
        ct.includes("audio") ||
        ct.includes("octet-stream") ||
        ct.includes("wav") ||
        ct.includes("mpeg") ||
        ct.includes("flac")
      ) {
        const blob = await res.blob();
        const audioUrl = URL.createObjectURL(blob);
        const src = res.headers.get("X-TTS-Source") || "suite";
        return {
          ok: true,
          audioUrl,
          blob,
          backend: `suite:${src}`,
          costUsdHint,
          format: req.format || (ct.includes("mpeg") ? "mp3" : "wav"),
        };
      }
      // JSON envelope with base64 / url
      const data = (await res.json()) as Record<string, unknown>;
      const extracted = extractAudio(data);
      if (extracted) {
        return {
          ok: true,
          audioUrl: extracted,
          backend: "suite",
          costUsdHint,
          format: req.format || "mp3",
        };
      }
      errors.push(`${a.path}: no audio in response`);
    } catch (e) {
      errors.push(`${a.path}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return {
    ok: false,
    error:
      errors.join(" · ") ||
      "Suite voice engines unavailable. Check sign-in / wallet, then Generate on Suite.",
    backend: "suite",
    costUsdHint,
  };
}

function extractAudio(data: Record<string, unknown>): string | null {
  if (typeof data.audio === "string") {
    const s = data.audio;
    return s.startsWith("data:") || s.startsWith("http") ? s : `data:audio/wav;base64,${s}`;
  }
  if (typeof data.audio_url === "string") return data.audio_url as string;
  if (typeof data.url === "string") return data.url as string;
  if (typeof data.b64_json === "string") {
    return `data:audio/wav;base64,${data.b64_json}`;
  }
  if (Array.isArray(data.data)) {
    for (const x of data.data as { url?: string; b64_json?: string }[]) {
      if (x.url) return x.url;
      if (x.b64_json) return `data:audio/wav;base64,${x.b64_json}`;
    }
  }
  return null;
}
